import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Content, ContentContainer, EndScreenLayer, FallbackVideo, OptionsIcon, PlaceholderGlow, PlaceholderImage, PlayerLayer, PlayerPlaceholder, PlayerShell, TextContainer, Title } from './styled';
import { Icon } from 'ui/styled';
import { WistiaPlayer as WistiaReactPlayer } from '@wistia/wistia-player-react';
import { parseCustomFieldHtml, parseStrapiImage } from 'utils';
import FileCard from 'components/Cards/File';
import ExternalResourceLink from 'components/ExternalResourceLink';
import useVideoAnalytics from 'hooks/useVideoAnalytics';

const CHECKPOINT_COUNT = 3
const SEEK_GUARD_SECONDS = 10

function hashString(value = '') {
  let hash = 0

  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index)
    hash |= 0
  }

  return Math.abs(hash)
}

function createSeededRandom(seed) {
  let state = seed || 1

  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296
    return state / 4294967296
  }
}

function buildCheckpoints(duration, seedKey) {
  if (!Number.isFinite(duration) || duration <= 0) return []

  const random = createSeededRandom(hashString(seedKey))
  const ranges = [
    [0.15, 0.3],
    [0.4, 0.55],
    [0.7, 0.85],
  ]

  return ranges.slice(0, CHECKPOINT_COUNT).map((range, index) => {
    const [startRatio, endRatio] = range
    const checkpointTime = duration * (startRatio + ((endRatio - startRatio) * random()))

    return {
      id: index + 1,
      reached: false,
      time: checkpointTime,
    }
  })
}

export default function WistiaPlayer({
  analytics,
  course,
  endScreen,
  onCompleted,
  showDetails = true,
  minHeight = '360px',
}) {
  const progressFiredOnce = useRef(false)
  const playerRef = useRef(null)
  const fallbackVideoRef = useRef(null)
  const checkpointsRef = useRef([])
  const lastTimeRef = useRef(0)
  const [mediaReady, setMediaReady] = useState(false)

  const playerKey = useMemo(() => `${course?.id || 'course'}-${course?.video_id || course?.video?.url || 'video'}`, [course?.id, course?.video?.url, course?.video_id])
  const videoAnalytics = useVideoAnalytics({
    enabled: !!analytics?.eventPrefix,
    eventPrefix: analytics?.eventPrefix,
    metadata: analytics?.metadata,
    sessionKey: analytics?.sessionKey || playerKey,
  })

  useEffect(() => {
    progressFiredOnce.current = false
    checkpointsRef.current = []
    lastTimeRef.current = 0
    setMediaReady(false)
  }, [playerKey])

  const ensureCheckpoints = useCallback((duration) => {
    if (checkpointsRef.current.length || !Number.isFinite(duration) || duration <= 0) return

    checkpointsRef.current = buildCheckpoints(duration, playerKey)
  }, [playerKey])

  const handleMediaReady = useCallback(() => {
    setMediaReady(true)
    const player = course?.video_id ? playerRef.current : fallbackVideoRef.current
    ensureCheckpoints(player?.duration)
  }, [course?.video_id, ensureCheckpoints])

  const getPlayerSnapshot = useCallback(() => {
    const player = course?.video_id ? playerRef.current : fallbackVideoRef.current
    const watchedSeconds = Number.isFinite(player?.currentTime) ? player.currentTime : 0
    const durationSeconds = Number.isFinite(player?.duration) ? player.duration : 0

    return {
      watchedSeconds,
      durationSeconds,
    }
  }, [course?.video_id])

  const handlePlay = useCallback(() => {
    videoAnalytics.trackStarted(getPlayerSnapshot())
  }, [getPlayerSnapshot, videoAnalytics])

  const handleEnded = useCallback(() => {
    const snapshot = getPlayerSnapshot()

    videoAnalytics.trackCompleted({
      ...snapshot,
      watchedSeconds: snapshot.durationSeconds || snapshot.watchedSeconds,
      progressPercent: 100,
    })

    if (progressFiredOnce.current || typeof onCompleted !== 'function') return

    ensureCheckpoints(snapshot.durationSeconds)

    if (
      checkpointsRef.current.length
      && checkpointsRef.current.every(checkpoint => checkpoint.reached)
    ) {
      progressFiredOnce.current = true
      onCompleted()
    }
  }, [ensureCheckpoints, getPlayerSnapshot, onCompleted, videoAnalytics])

  const handleTimeUpdate = () => {
    const player = course?.video_id ? playerRef.current : fallbackVideoRef.current
    if (!player) return

    const duration = player.duration
    const current = player.currentTime
    const previousTime = lastTimeRef.current

    lastTimeRef.current = current

    if (!Number.isFinite(duration) || !Number.isFinite(current)) return

    videoAnalytics.trackProgress({
      watchedSeconds: current,
      durationSeconds: duration,
    })

    if (progressFiredOnce.current || typeof onCompleted !== 'function') return

    ensureCheckpoints(duration)

    const nextCheckpointIndex = checkpointsRef.current.findIndex(checkpoint => !checkpoint.reached)
    const finalWindowStart = duration - 5
    const isForwardSeek = previousTime > 0 && current - previousTime > SEEK_GUARD_SECONDS
    const crossedFinalWindow = previousTime < finalWindowStart && current >= finalWindowStart

    if (nextCheckpointIndex === -1) {
      if (!isForwardSeek && crossedFinalWindow) {
        progressFiredOnce.current = true
        videoAnalytics.trackCompleted({
          watchedSeconds: duration || current,
          durationSeconds: duration,
          progressPercent: 100,
        })
        onCompleted()
      }
      return
    }

    const nextCheckpoint = checkpointsRef.current[nextCheckpointIndex]
    const crossedCheckpoint = previousTime < nextCheckpoint.time && current >= nextCheckpoint.time

    if (isForwardSeek || !crossedCheckpoint) return

    checkpointsRef.current = checkpointsRef.current.map((checkpoint, index) => (
      index !== nextCheckpointIndex
        ? checkpoint
        : { ...checkpoint, reached: true }
    ))
  }

  const parsedContent = parseCustomFieldHtml(course?.description || course?.content || '')
  const fallbackVideoUrl = course?.video?.url ? parseStrapiImage(course?.video?.url) : null
  const posterUrl = course?.image?.url
    ? parseStrapiImage(course.image.url)
    : course?.cover || (course?.video_id ? `https://fast.wistia.com/embed/medias/${course.video_id}/swatch` : null)

  return (
    <Container>
      <PlayerShell minHeight={minHeight}>
        <PlayerPlaceholder hidden={mediaReady}>
          {!posterUrl ? null : <PlaceholderImage alt={course?.title || 'Video'} src={posterUrl} />}
          <PlaceholderGlow />
        </PlayerPlaceholder>

        <PlayerLayer visible={mediaReady}>
          {course?.video_id ? (
            <WistiaReactPlayer
              key={playerKey}
              ref={playerRef}
              mediaId={course?.video_id}
              onApiReady={handleMediaReady}
              onCanPlay={handleMediaReady}
              onEnded={handleEnded}
              onLoadedData={handleMediaReady}
              onPlay={handlePlay}
              onSecondChange={handleTimeUpdate}
              onTimeUpdate={handleTimeUpdate}
              style={{ width: '100%', height: '100%' }}
              swatch={false}
            />
          ) : !fallbackVideoUrl ? null : (
            <FallbackVideo
              controls
              ref={fallbackVideoRef}
              onCanPlay={handleMediaReady}
              onEnded={handleEnded}
              onLoadedData={handleMediaReady}
              onPlay={handlePlay}
              onTimeUpdate={handleTimeUpdate}
              preload="metadata"
            >
              <source src={fallbackVideoUrl} />
            </FallbackVideo>
          )}
        </PlayerLayer>

        {!endScreen ? null : (
          <EndScreenLayer>
            {endScreen}
          </EndScreenLayer>
        )}
      </PlayerShell>

      {!showDetails ? null : (
        <Content>
          <TextContainer>
            <OptionsIcon><Icon icon="video" /></OptionsIcon>
            <Title>{course?.title}</Title>
          </TextContainer>
          <ContentContainer>
            {!course?.file ? null : <FileCard file={course} />}
            <ExternalResourceLink course={course} />
            <Title dangerouslySetInnerHTML={parsedContent} />
          </ContentContainer>
        </Content>
      )}
    </Container>
  )
}
